// Histórico por alojamiento: cuánto lleva cada uno en cartera y cómo ha ido.

import { useMemo, useState } from 'react';
import { ArrowUp, ArrowDown, ArrowUpDown, ExternalLink } from 'lucide-react';
import type { HistoricoReservasResult, HistoricoPropiedad } from '@/lib/types';
import { ZONE_LABELS, classNames } from '@/lib/utils';
import { Tarjeta, fmtMoney, fmtNum, fmtPct, fmtDia } from './comun';

type Clave = keyof Pick<HistoricoPropiedad, 'propiedad' | 'zona' | 'reservas' | 'noches' | 'facturacion' | 'adr' | 'estanciaMedia' | 'primeraEntrada' | 'ultimaEntrada' | 'tasaCancelacion'>;
type Orden = { clave: Clave; dir: 'asc' | 'desc' };

const TEXTO: Clave[] = ['propiedad', 'zona', 'primeraEntrada', 'ultimaEntrada'];

export default function SeccionPropiedades({ data }: { data: HistoricoReservasResult }) {
  const [orden, setOrden] = useState<Orden>({ clave: 'reservas', dir: 'desc' });

  const filas = useMemo(() => {
    const lista = [...data.propiedades];
    const m = orden.dir === 'asc' ? 1 : -1;
    lista.sort((a, b) => {
      const va = a[orden.clave];
      const vb = b[orden.clave];
      if (TEXTO.includes(orden.clave)) return m * String(va ?? '').localeCompare(String(vb ?? ''));
      return m * (Number(va) - Number(vb));
    });
    return lista;
  }, [data, orden]);

  const cambiar = (clave: Clave) =>
    setOrden((prev) =>
      prev.clave === clave ? { clave, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { clave, dir: TEXTO.includes(clave) ? 'asc' : 'desc' },
    );

  const Cab = ({ label, clave, derecha }: { label: string; clave: Clave; derecha?: boolean }) => {
    const activa = orden.clave === clave;
    const Icono = !activa ? ArrowUpDown : orden.dir === 'asc' ? ArrowUp : ArrowDown;
    return (
      <th className={classNames('py-2 font-medium', derecha ? 'text-right' : 'text-left')}>
        <button
          onClick={() => cambiar(clave)}
          className={classNames('inline-flex items-center gap-1 hover:text-sereno-dark transition-colors', derecha && 'flex-row-reverse', activa ? 'text-sereno-dark' : 'text-sereno-dark/50')}
        >
          <span>{label}</span>
          <Icono size={12} className={activa ? 'text-sereno-dark' : 'text-sereno-dark/30'} />
        </button>
      </th>
    );
  };

  return (
    <Tarjeta
      titulo="Histórico por alojamiento"
      info="Reservas confirmadas de cada alojamiento en todo el histórico filtrado. Primera y última entrada acotan su vida en cartera (la última puede ser futura). «Sin asignar» agrupa listings de Hostify que no están dados de alta como propiedad en el CRM."
      className="mb-6"
      extra={<span className="text-xs text-sereno-dark/50 whitespace-nowrap">{fmtNum(data.propiedades.length)} alojamientos</span>}
    >
      <div className="overflow-x-auto -mx-6 px-6">
        <table className="w-full text-sm whitespace-nowrap">
          <thead>
            <tr className="text-xs text-sereno-dark/50 border-b border-sereno-dark/10">
              <Cab label="Alojamiento" clave="propiedad" />
              <Cab label="Zona" clave="zona" />
              <Cab label="Reservas" clave="reservas" derecha />
              <Cab label="Noches" clave="noches" derecha />
              <Cab label="Facturación" clave="facturacion" derecha />
              <Cab label="ADR" clave="adr" derecha />
              <Cab label="Estancia" clave="estanciaMedia" derecha />
              <Cab label="Cancelación" clave="tasaCancelacion" derecha />
              <Cab label="Primera entrada" clave="primeraEntrada" derecha />
              <Cab label="Última entrada" clave="ultimaEntrada" derecha />
            </tr>
          </thead>
          <tbody>
            {filas.map((p) => (
              <tr key={`${p.propertyId ?? ''}|${p.propiedad}`} className={classNames('border-b border-sereno-dark/5 last:border-0', p.propertyId && !p.activa && 'text-sereno-dark/60')}>
                <td className="py-2">
                  <span className="inline-flex items-center gap-1.5">
                    {p.propiedad}
                    {p.propertyId && !p.activa && <span className="badge bg-sereno-blue/30 text-sereno-dark/60">inactiva</span>}
                    {p.listingId && (
                      <a
                        href={`https://app.hostify.com/listings/view/${p.listingId}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={`Ver listing ${p.listingId} en Hostify`}
                        className="text-sereno-dark/30 hover:text-sereno-dark transition-colors"
                      >
                        <ExternalLink size={13} />
                      </a>
                    )}
                  </span>
                </td>
                <td className="py-2 text-sereno-dark/60">{ZONE_LABELS[p.zona] || p.zona || '—'}</td>
                <td className="py-2 text-right tabular-nums">{fmtNum(p.reservas)}</td>
                <td className="py-2 text-right tabular-nums">{fmtNum(p.noches)}</td>
                <td className="py-2 text-right tabular-nums font-semibold">{fmtMoney(p.facturacion)}</td>
                <td className="py-2 text-right tabular-nums">{fmtMoney(p.adr)}</td>
                <td className="py-2 text-right tabular-nums">{fmtNum(p.estanciaMedia, 1)} n.</td>
                <td className="py-2 text-right tabular-nums">
                  {fmtPct(p.tasaCancelacion)} <span className="text-xs text-sereno-dark/40">({fmtNum(p.canceladas)})</span>
                </td>
                <td className="py-2 text-right tabular-nums">{fmtDia(p.primeraEntrada)}</td>
                <td className="py-2 text-right tabular-nums">{fmtDia(p.ultimaEntrada)}</td>
              </tr>
            ))}
            {filas.length === 0 && (
              <tr>
                <td colSpan={10} className="py-4 text-center text-sereno-dark/40">Sin datos</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </Tarjeta>
  );
}
